import $store from '@/store'
import StrUtils from '@/utils/StrUtils'
import ColumnUtils from '@/utils/ColumnUtils'
import * as RateUtil from '@/class/cargo/model/utils/RateUtil'
import lang from '@/lang'
import * as PriceConstant from '@/class/cargo/model/parameters/PriceConstant'
import { QuotationFclDetail } from '@/class/model/price/QuotationFclDetail'
import { QuotationLclDetail } from '@/class/model/price/QuotationLclDetail'
import { QuotationAirDetail } from '@/class/model/price/QuotationAirDetail'
import checkPermission from '@/utils/permission'
import { MultiCompanyUtil } from '@/class/cargo/model/utils/MultiCompanyUtil'
import { getObjectByCache, getSpecificBasicData } from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import * as NumberUtils from '@/utils/NumberUtils'

import moment from 'moment'
/**
 * 整个运价模块的公用工具类
 */
export class PriceUtils {
  constructor() {
  }

  static TIERS = [{ label: 'Cost', value: '0' }, { label: 'Tier 1', value: '1' }, { label: 'Tier 2', value: '2' }, { label: 'Tier 3', value: '99' }]
  /**
   * 运价等级数据源,运价不分层时，运价等级定死在1级
   * @param needMarkup
   * @param showTier
   * @param tier
   * @returns {Promise<void>}
   */
  static getTiers(needMarkup, showTier, tier) {
    let tiers = null
    if (needMarkup) {
      if (showTier) {
        switch (tier) {
          case 0:
            tiers = this.TIERS.slice(0)
            break
          case 1:
            tiers = this.TIERS.slice(1)
            break
          case 2:
            tiers = this.TIERS.slice(2)
            break
          default:
            tiers = this.TIERS.slice(3)
            break
        }
      } else {
        switch (tier) {
          case 0:
            tiers = this.TIERS.slice(0, 1)
            break
          case 1:
            tiers = this.TIERS.slice(1, 2)
            break
          case 2:
            tiers = this.TIERS.slice(2, 3)
            break
          default:
            tiers = this.TIERS.slice(3, 4)
            break
        }
      }
    } else {
      tiers = [{ label: 'Tier 1', value: '1' }]
    }
    return tiers
  }

  static remarkLabelFunc(data, value) {
    if (value === null) return null
    if (data['isPriceFclAddonEx']) {
      const result = []
      if (data.remark_a) {
        result.push('From ' + data.pol_a + ' to ' + data.pod_a + ':')
        result.push(data.remark_a)
      }
      if (data.remark) {
        result.push('From ' + data.pol + ' to ' + data.pod + ':')
        result.push(data.remark)
      }
      if (data.remark_b) {
        result.push('From ' + data.pol_b + ' to ' + data.pod_b + ':')
        result.push(data.remark_b)
      }
      return result.join('\n')
    }
    return value
  }
  static daysColumnLabelFunc(data, value) {
    if (data === null) return ''
    if (data['isPriceFclAddonEx']) {
      return (this.toNum(value) + this.toNum(data.days_a) + this.toNum(data.days_b)) + '+'
    }
    if (Number(value) === 0) return '-'
    return value
  }
  static carrierLabFunc(data, value) {
    if (value === null || !data) return ''
    const tenantId = data.tenantId
    const sourcePriceUuid = data.sourcePriceUuid
    const shareCarrierScacCode = data.carrierScacCode
    const carrierName = $store.getters.language === 'zh' ? data['carrierName'] : data['carrierEnName']
    if (tenantId === $store.getters.tenantId) {
      if (!StrUtils.isEmpty(sourcePriceUuid)) {
        if (!StrUtils.isEmpty(shareCarrierScacCode)) {
          return ColumnUtils.companyLabelFun(shareCarrierScacCode, 'shortName', 'scac')
        }
      } else {
        return ColumnUtils.companyShortNameLabelFun(value)
      }
    } else {
      if (!StrUtils.isEmpty(shareCarrierScacCode)) {
        value = ColumnUtils.companyLabelFun(shareCarrierScacCode, 'shortName', 'scac')
        if (value) {
          return value
        } else {
          return shareCarrierScacCode
        }
      } else if (!StrUtils.isEmpty(carrierName)) {
        // 分享方也没有配置scac场景
        return carrierName
      }
    }
    return ''
  }
  static dataGridColumnPaymentFunc(value) {
    switch (!value ? 'P' : value) {
      case 'P':
        return lang.t('Pricing.Prepaid')
      case 'C':
        return lang.t('Pricing.Collect')
      case 'B':
        return lang.t('Pricing.Both')
    }
    return ''
  }
  static vatLabelFunc(data, value, level) {
    const num = level === 0 ? data.costVat : data.sellVat
    if (Number(num) === 0) return '0'
    if (!isNaN(num)) {
      if (num === PriceConstant.NIL) return ''
      return StrUtils.numberFormatFunc(num)
    }
    return ''
  }
  static priceByViewColumnLabelFunc(view, params, level, targetCurrency, authLevel) {
    switch (view) {
      case 0:
        return this.priceColumnLabelFunc_nolimit(params.value)
      case 1:
        return this.priceColumnLabelFunc_effective(params, level, authLevel)
      case 2:
        return this.priceColumnLabelFunc_history(params, level, targetCurrency, authLevel)
      case 3:
        return this.priceColumnLabelFunc_history(params, level, targetCurrency, authLevel)
      case 4:
        return this.priceByViewColumnLabelFunc_nolimit(params, targetCurrency)
      default:
        return ''
    }
  }
  static priceColumnLabelFunc(params, level, targetCurrency, authLevel) {
    if (params.value === null) return ''
    if (params.data && params.data['isPriceFclAddonEx']) {
      return this.priceColumnLabelFunc_query_ex(params, level, targetCurrency)
    }
    return this.priceColumnLabelFunc_query(params, level, targetCurrency, authLevel)
  }
  static priceColumnLabelFunc_query_ex(params, level, targetCurrency) {
    const num = this.calcPrice(params, level)
    return this.changeTargetCurrency(num, params, targetCurrency)
  }
  static changeTargetCurrency(num, params, targetCurrency) {
    if (num === PriceConstant.NIL) return ''
    if (targetCurrency && params.data['currency'] !== targetCurrency) {
      const rate = RateUtil.getForeignExchangeRateSafely(params.data['currency'], targetCurrency, new Date(), 0)
      if (rate > 0) {
        const price = num / rate
        if (price < 0) return StrUtils.substitute('({0})', StrUtils.numberFormatFunc(-price))
        return StrUtils.numberFormatFunc(price)
      } else {
        return '-'
      }
    } else {
      if (num === '') return ''
      if (num < 0) return StrUtils.substitute('({0})', StrUtils.numberFormatFunc(-num))
    }
    return StrUtils.numberFormatFunc(num)
  }
  static priceColumnLabelFunc_query(params, level, targetCurrency, authLevel) {
    if (checkPermission(['PFT']) || checkPermission(['PFA'])) { // 是否允许批准
      return this.priceColumnLabelFunc_effective(params, level, authLevel, targetCurrency)
    }
    let num = NaN
    let newField = ''
    newField = params.colDef.field.replace('cost', this.priceFieldMap[level === 0 ? 1 : level])
    num = params.data[newField]
    if (num === 0) return '0'
    if (!isNaN(num)) {
      return this.changeTargetCurrency(num, params, targetCurrency)
    }
    return ''
  }
  static priceColumnLabelFunc_history(params, level, targetCurrency, authLevel) {
    if (checkPermission(['PFA'])) { // 是否允许批准
      return this.priceColumnLabelFunc_effective(params, level, authLevel)
    }
    if (authLevel === 0) {
      authLevel = 1
    }
    const newField = params.colDef.field.replace('cost', this.priceFieldMap[authLevel])
    const num = params.data[newField]
    if (num === 0) return '0'
    if (!isNaN(num)) {
      return this.changeTargetCurrency(num, params, targetCurrency)
    }
    return ''
  }
  static priceColumnLabelFunc_effective(params, level, authLevel, targetCurrency) {
    const officeIds = MultiCompanyUtil.getInstance().getOfficeIDs2('PriceFcl', 'PF')
    const officeIDMap = {}
    officeIds.forEach((id, index) => {
      officeIDMap[id] = index
    })
    if (authLevel > level) return '' // 运价等级不够
    if (level === 0 && (officeIds && officeIds[0] === 0) && !officeIDMap.hasOwnProperty(params.data['branchId'])) return '' // 不可以看其他分公司成本
    const newField = level === 0 ? params.colDef.field : params.colDef.field.replace('cost', this.priceFieldMap[level])
    const num = params.data[newField]
    if (num === 0) return '0'
    if (isNaN(num) || num === PriceConstant.NIL) {
      return ''
    }
    return this.changeTargetCurrency(num, params, targetCurrency)
  }
  static priceFieldMap = { '0': 'cost', '1': 'one', '2': 'two', '99': 'three' }
  static FREQUENCYS = ['1', '2', '3', '4', '5', '6', '7']
  static frequencyLabelFunc(data, field) {
    let result = ''
    for (const i of this.FREQUENCYS) {
      if (data[field + i]) {
        result += i + '/'
      }
    }
    if (result === '') {
      return ''
    }
    return result.substring(0, result.length - 1)
  }
  static calcPrice(params, level) {
    let num = ''
    let newField = ''
    newField = params.colDef.field.replace('cost', this.priceFieldMap[level === 0 ? 1 : level])
    num = params.data[newField]
    if (num === PriceConstant.NIL) return PriceConstant.NIL

    const addonField = params.colDef.field.replace('cost', 'sum')
    if (params.data[addonField + '_a'] === PriceConstant.NIL) return PriceConstant.NIL
    if (params.data[addonField + '_b'] === PriceConstant.NIL) return PriceConstant.NIL
    if (!num) return ''
    return num + this.toNum(params.data[addonField + '_a']) + this.toNum(params.data[addonField + '_b'])
  }
  static toNum(value) {
    if (!value) return 0
    return Number(value)
  }
  static getForeignExchangeRate(sourceCode, targetCode) {
    const rate = RateUtil.getForeignExchangeRateSafely(sourceCode, targetCode, new Date(), 0)
    const localCurrency = RateUtil.localCurrencyByOffice()
    if (sourceCode === localCurrency && targetCode === 'USD') {
      return Math.round(rate)
    }
    return rate
  }
  static shareColumnLabelFunc(value) {
    if (value === 'SELF') return lang.t('Pricing.Self')
    if (value === 'SHARE') return lang.t('Pricing.Share')
    return ''
  }
  static trendLabelFunc(value) {
    if (value === 1) return '↑'
    if (value === -1) return '↓'
    return ''
  }
  static priceStateLabelFunc(value) {
    switch (value) {
      case 0:
        return lang.t('Pricing.PriceState_Common')
      case 1:
        return lang.t('Pricing.PriceState_Suspend')
      case 2:
        return lang.t('Pricing.PriceState_Reject')
      case 3:
        return lang.t('Pricing.PriceState_Inquiry')
      case 4:
        return lang.t('Pricing.PriceState_Lack')
      case 5:
        return lang.t('Pricing.PriceState_Overbooked')
    }
    return lang.t('Pricing.PriceState_Common')
  }
  static dataGridColumnclnTimeFunc(value) {
    if (value > 0) {
      return value
    }
    return ''
  }
  static resetFilter(filters) {
    for (const filter in filters) {
      const type = typeof filters[filter]
      if (type === 'string') {
        filters[filter] = ''
        continue
      }
      if (type === 'number') {
        filters[filter] = 0
        continue
      }
      if (type === 'boolean') {
        filters[filter] = false
        continue
      }
      if (Array.isArray(filters[filter])) {
        filters[filter] = []
        continue
      }
      if (type === 'object') {
        filters[filter] = null
      }
    }
    return filters
  }
  static gradeMinLabelFunc(params) {
    if (params.data.gradeMin !== PriceConstant.NIL) {
      if (params.data.gradeMax !== PriceConstant.NIL) {
        return params.data.gradeMin + '-' + params.data.gradeMax + this.getMeasureLabel(params.data.gradeUnit)
      } else {
        return '＞' + params.data.gradeMin + this.getMeasureLabel(params.data.gradeUnit)
      }
    }
    if (params.data.gradeMax !== PriceConstant.NIL) {
      return '≤' + params.data.gradeMax + this.getMeasureLabel(params.data.gradeUnit)
    }
    return ''
  }
  static measureMap = { 'WGT': 'TON', 'WGTX': 'RT', 'VOL': 'CBM' }
  static getMeasureLabel(unit) {
    if (!unit || !this.measureMap.hasOwnProperty(unit)) {
      return unit
    }
    return this.measureMap[unit]
  }
  static priceColumnLabelFunc_nolimit(num) {
    if (num === 0) return '0'
    if (!num) return ''
    if (!isNaN(num)) {
      if (num === PriceConstant.NIL) return ''
      if (num < 0) {
        return StrUtils.substitute('({0})', StrUtils.numberFormatFunc(-num))
      }
      return StrUtils.numberFormatFunc(num)
    }
    return ''
  }
  static priceByViewColumnLabelFunc_nolimit(params, targetCurrency) {
    const num = params.value
    return (!isNaN(num)) ? this.changeTargetCurrency(num, params, targetCurrency) : ''
  }
  static recordTenant(results, tenantId) {
    results.forEach(item => {
      item.tenantId = tenantId
    })
  }
  static aircraftLabelFunction(value) {
    if (!value) return ''
    switch (value) {
      case 'C':
        return lang.t('Pricing.CargoAircraft')
      case 'A':
        return lang.t('Pricing.Airliner')
      case 'H':
        return lang.t('Pricing.HeadLiftAircraft')
      case '货机':
        return lang.t('Pricing.CargoAircraft')
      case '客机':
        return lang.t('Pricing.Airliner')
      case '客机/货机':
        return lang.t('Pricing.AircraftOrFreighter')
      case '货机/客机':
        return lang.t('Pricing.FreighterOrAircraft')
      case 'CARGO AIRCRAFT':
        return lang.t('Pricing.CargoAircraft')
      case 'AIRLINER':
        return lang.t('Pricing.Airliner')
      case 'AIRLINER(NB)':
        return lang.t('Pricing.AirlinerNB')
    }
    return value
  }
  static numberColumnLabelFunc_TACT(params, _showCurrency, _targetCurrency, _allIn) {
    let num = params.value
    if (isNaN(params.value)) {
      num = 0
    }
    num += this.addAllInFee(params.data, params.colDef.field, _allIn)
    if (num === 0) return '0'
    if (!isNaN(num)) {
      if (num === PriceConstant.TACT) return 'TACT'
      if (num === PriceConstant.NIL) return 'NIL'
      return this.numberColumn(params, num, _showCurrency, _targetCurrency)
    }
    return ''
  }
  static convertCurrencyColumnLabelFunc(params, _showCurrency, _targetCurrency) {
    const num = params.value
    if (num === 0) return '0'
    if (!isNaN(num)) {
      if (num === PriceConstant.NIL) return 'NIL'
      return this.numberColumn(params, num, _showCurrency, _targetCurrency)
    }
    return ''
  }
  static numberColumn(params, num, _showCurrency, _targetCurrency) {
    if (!_showCurrency) {
      if (params.data['currency'] !== _targetCurrency) {
        const rate = RateUtil.getForeignExchangeRateSafely(params.data['currency'], _targetCurrency, new Date(), 0)
        if (rate > 0) {
          const price = num / rate
          if (price < 0) {
            return StrUtils.substitute('({0})', StrUtils.numberFormatFunc(-price))
          }
          return StrUtils.numberFormatFunc(price)
        }
      } else {
        if (num < 0) {
          return StrUtils.substitute('({0})', StrUtils.numberFormatFunc(-num))
        }
      }
    }
    return StrUtils.numberFormatFunc(num)
  }
  static priceCols = ['step', 'p45', 'p100', 'p300', 'p500', 'p1000']
  static addAllInFee(data, field, _allIn) {
    let fee = 0
    if (_allIn && this.priceCols.indexOf(field) > -1) {
      const fsc = data['fsc']
      const ssc = data['ssc']
      const tc = data['tc']
      if (!isNaN(fsc)) {
        fee += fsc
      }
      if (!isNaN(ssc)) {
        fee += ssc
      }
      if (!isNaN(tc)) {
        fee += tc
      }
    }
    return fee
  }

  /**
   * 运价来源，需要初始化tenantUtil，new TenantUtil
   * @param data
   * @param value
   * @returns {string|*}
   */
  static bookingAgentLabelFunc(data, value, tenantUtil) {
    if (value === null) return ''
    const tenantId = data.tenantId
    const sourcePriceUuid = data.sourcePriceUuid
    if (tenantId === $store.getters.tenantId) {
      if (!StrUtils.isEmpty(sourcePriceUuid)) {
        return tenantUtil.getTenantNameById(value)
      } else {
        return ColumnUtils.companyShortNameLabelFun(value)
      }
    } else {
      return tenantUtil.getTenantNameById(tenantId)
    }
  }
  static setCellColor(data, isPriceFcl, dataField) {
    if (data) {
      const isLineThrough = this.isLineThrough(data, isPriceFcl, dataField)
      if (data.hasOwnProperty('trend')) {
        const trend = data.trend
        const style = {}
        if (isLineThrough) {
          style['text-decoration-line'] = 'line-through'
        }
        switch (trend) {
          case -1:
            style['color'] = 'green'
            break
          case 0:
            style['color'] = 'black'
            break
          case 1:
            style['color'] = 'red'
            break
        }
        return style
      }
    }
  }
  static isLineThrough(data, isPriceFcl, dataField) {
    let isLineThrough = false
    if (data) {
      if (data.hasOwnProperty('priceState') && data['priceState'] === 2) {
        if (isPriceFcl) {
          let rejectField = dataField.replace('cost', 'reject')
          rejectField = rejectField === 'rejecthc' ? 'rejectHC' : rejectField
          isLineThrough = data.hasOwnProperty(rejectField) && data[rejectField]
        } else {
          isLineThrough = true
        }
      } else {
        isLineThrough = false
      }
      return isLineThrough
    }
    return isLineThrough
  }
  static setTooltip(data, isPriceFcl, dataField) {
    const isLineThrough = this.isLineThrough(data, isPriceFcl, dataField)
    return isLineThrough ? lang.t('Pricing.reject') : ''
  }
  static split(value, isStr) {
    if (value) {
      const result = value.split(',')
      const check = []
      const data = []
      for (const i of result) {
        if (StrUtils.isEmpty(i)) {
          continue
        }
        data.push(isStr ? i : Number(i))
        check.push(isStr ? i : Number(i))
      }
      return { check: check, data: data }
    }
    return []
  }
  static calcHashCode(data) {
    let pt = ColumnUtils.getSeaport(data.pol)
    if (pt) {
      data.rol = pt.region
      data.col = pt.countryCode
    } else {
      data.rol = 'NIL'
      data.col = 'NIL'
    }
    pt = ColumnUtils.getSeaport(data.pod)
    if (pt) {
      data.rod = pt.region
      data.cod = pt.countryCode
    } else {
      data.rod = 'NIL'
      data.cod = 'NIL'
    }
  }
  static numberColumnLabelFunc(data, num) {
    if (!data || !num) return ''
    return this.formatNumber(num)
  }
  static numberColumnLabelFuncShowZero(data, num) {
    if (!data || num === '') return ''
    return this.formatNumber(Number(num))
  }
  static formatNumber(num) {
    if (!isNaN(num)) {
      if (num === 0) {
        return '0.00'
      } else if (num < 0) {
        return StrUtils.substitute('({0})', StrUtils.numberFormatFunc(-num))
      } else {
        return StrUtils.numberFormatFunc(num)
      }
    }
    return ''
  }
  static dataGridColumnCarrierFunc(data, cpyIds) {
    if (!data || !cpyIds) return ''
    if (cpyIds) {
      const cpys = cpyIds.toString().split(',')
      let company = null
      const rs = []
      for (const cpy in cpys) {
        const cmpIdx = parseInt(cpys[cpy])
        company = cmpIdx > 0 ? getObjectByCache(basicCacheKeys.basic_data_Company, 'cmpIdx', cmpIdx) : null
        if (company && company.shortName) rs.push(company.shortName)
      }
      if (rs.length > 0) return rs.join()
    }
    return ''
  }
  /**
   * 求两个数组交集
   */
  static getOverlap(s1, s2) {
    if (s1 === null || s2 === null) return null
    const rs = []
    const map = {}
    for (const s of s1) {
      map[s] = 1
    }
    for (const s of s2) {
      if (map.hasOwnProperty(s)) {
        rs.push(s)
      }
    }
    return rs.length > 0 ? rs : null
  }
  static getPolField(type) {
    if (type < 0 && type > PriceConstant.POL_TYPE.length - 1) return ''
    return PriceConstant.POL_TYPE[type]
  }
  static getPodField(type) {
    if (type < 0 && type > PriceConstant.POD_TYPE.length - 1) return ''
    return PriceConstant.POD_TYPE[type]
  }
  /**
   * 是否是返回了部分结果
   */
  static isReturnedPartOfResults(isLimited, len) {
    return isLimited && (len === PriceConstant.MAX_QUERY_COUNT + 1)
  }

  static numberWithNILColumnLabelFunc(data, dataField) {
    if (!data || !data[dataField]) return 0.00
    const rate = data['rate']
    const num = data[dataField]
    if (!num || isNaN(num)) return 0.00
    if (!isNaN(num)) {
      if (num === 0) return 0.00
      if (num === PriceConstant.NIL || num === PriceConstant.TACT || num === PriceConstant.MINIMUN) return ''
    }
    if (dataField === 'amount' && (rate === PriceConstant.NIL || rate === PriceConstant.TACT || rate === PriceConstant.MINIMUN)) {
      return 0.00
    }
    return this.formatNumber(data[dataField])
  }
  static chargeTypeLabelFunc(params) {
    const type = params.value
    if (!type) return null
    switch (type) {
      case 'S':
        return lang.t('Pricing.Surcharge')
      case 'L':
        return lang.t('Pricing.LocalCharge')
      case 'D':
        return lang.t('Pricing.DesCharge')
      default:
        return null
    }
  }
  static chargeScaleCodeLabelFunc(params, isZh = true) {
    const code = params.value
    if (!code) return null
    const chargeItem = getObjectByCache(TYPE_NAMES.basic_data_ChargeItem, 'scaleCode', code)
    if (chargeItem) {
      return isZh ? chargeItem.zhName : chargeItem.enName
    } else {
      return params.data.shareSurchargeName
    }
  }
  static vatLabelFunc2(params, showCost) {
    const { data } = params
    const num = showCost ? data.costVat : data.sellVat

    if (num === 0) return 0
    if (!isNaN(num)) {
      if (num === PriceConstant.NIL) return ''
      return NumberUtils.roundHalfUp(num).toFixed(2)
    }
    return ''
  }
  static priceByViewColumnLabelFunc_nolimit2(params, targetCurrency, showCost) {
    if (!params.value) return ''
    let field = params.colDef.field
    if (showCost) {
      if (field !== 'casePrice') {
        field = field.replace('sum', 'cost')
      } else {
        field = 'costCasePrice'
      }
    }
    const num = params.data[field]
    return (!isNaN(num)) ? this.changeTargetCurrency(num, params, targetCurrency) : ''
  }

  /**
   * 数据复制（不为空的数据）
   * @param sources 来源数据
   * @param targets 目标数据
   */
  static copyNotEmptyData(sources, targets) {
    for (const source in sources) {
      const type = typeof sources[source]
      const value = sources[source]
      // 为空字符串时不赋值
      if (type === 'string' && StrUtils.isEmpty(value)) {
        continue
      }
      // 为空数组时不赋值
      if (Array.isArray(value) && value.length === 0) {
        continue
      }
      // 为空对象时不赋值
      if (type === 'object' && !value) {
        continue
      }
      targets[source] = value
    }
  }
  static priceNoTipFunc(item, tenantUtil) {
    const tenantId = item.tenantId
    if (tenantId === $store.getters.tenantId) {
      return this.getOfficeById(item.branchId)
    } else {
      return tenantUtil.getTenantNameById(tenantId)
    }
  }
  static getOfficeById(branchId) {
    const ofc = getObjectByCache(basicCacheKeys.basic_data_Office_All, 'officeId', branchId)
    if (ofc) {
      return $store.getters.language === 'zh' ? ofc.chineseName : ofc.englishName
    }
    return ''
  }
  static carrierTipFunc(data) {
    const language = $store.getters.language === 'zh' ? 'chineseName' : 'englishName'
    const sourcePriceUuid = data['sourcePriceUuid']
    const carrierScacCode = data['carrierScacCode']
    const carrierName = $store.getters.language === 'zh' ? data['carrierName'] : data['carrierEnName']
    if (data.tenantId === $store.getters.tenantId) {
      if (!StrUtils.isEmpty(sourcePriceUuid)) {
        if (StrUtils.isEmpty(carrierScacCode)) {
          return ''
        }
        return ColumnUtils.resolveColumnLabel(basicCacheKeys.basic_data_Company,
          'scac', carrierScacCode, language)
      } else {
        return ColumnUtils.resolveColumnLabel(basicCacheKeys.basic_data_Company,
          'cmpIdx', data['carrier'], language)
      }
    } else {
      if (!StrUtils.isEmpty(carrierScacCode)) {
        return ColumnUtils.resolveColumnLabel(basicCacheKeys.basic_data_Company,
          'scac', carrierScacCode, language)
      } else if (!StrUtils.isEmpty(carrierName)) {
        return carrierName
      }
    }
    return ''
  }
  static shareCarrierTipFunc(data) {
    const language = $store.getters.language === 'zh' ? 'chineseName' : 'englishName'
    const cpyIds = data.shareCarrier
    if (cpyIds) {
      const companyIds = cpyIds.split(',')
      let company = null
      const rs = []
      for (const cpy of companyIds) {
        if (!StrUtils.isEmpty(cpy)) {
          company = ColumnUtils.resolveColumnLabel(basicCacheKeys.basic_data_Company,
            'cmpIdx', Number(cpy), language)
          if (company) {
            rs.push(company)
          }
        }
      }
      return rs.join('\n')
    }
    return ''
  }
  static getFileName(fileName) {
    const day = moment().format('YYYYMMDDHHmmss')
    if (fileName.indexOf('.xls') > -1) {
      return fileName.substr(0, fileName.length - 4) + day
    }
    return fileName + day
  }

  static numberColumnLabelFuncQuotation_TACT(data, dataField) {
    if (!data[dataField]) return ''
    const num = data[dataField]
    if (num === 0.0) return 0
    if (!isNaN(num)) {
      if (num === PriceConstant.TACT) return 'TACT'
      if (num === PriceConstant.NIL) return 'NIL'
      if (num < 0) return StrUtils.substitute('({0})', StrUtils.numberFormatFunc(Number(-num)))
      return StrUtils.numberFormatFunc(Number(num))
    }
    return ''
  }

  static createQuotationFclDetail(priceFcl, level) {
    const qf = new QuotationFclDetail().fillDefaultValue().getData()
    qf.pf = priceFcl
    qf.priceUuid = priceFcl.uuid
    qf.priceNo = priceFcl.priceNo
    qf.direct = priceFcl.direct
    qf.vat = priceFcl.vat
    qf.dock = priceFcl.dock
    qf.share = priceFcl.share

    qf.level = level
    qf.pol = priceFcl.pol
    qf.pod = priceFcl.pod
    qf.via = priceFcl.via
    qf.carrier = priceFcl.carrier
    qf.eqType = priceFcl.eqType
    switch (Number(level)) {
      case 0:
        qf.eq20 = priceFcl.cost20 === PriceConstant.NIL ? '' : String(priceFcl.cost20)
        qf.eq40 = priceFcl.cost40 === PriceConstant.NIL ? '' : String(priceFcl.cost40)
        qf.eqHc = priceFcl.costhc === PriceConstant.NIL ? '' : String(priceFcl.costhc)
        qf.eq45 = priceFcl.cost45 === PriceConstant.NIL ? '' : String(priceFcl.cost45)
        break
      case 1:
        qf.eq20 = priceFcl.one20 === PriceConstant.NIL ? '' : String(priceFcl.one20)
        qf.eq40 = priceFcl.one40 === PriceConstant.NIL ? '' : String(priceFcl.one40)
        qf.eqHc = priceFcl.oneHC === PriceConstant.NIL ? '' : String(priceFcl.oneHC)
        qf.eq45 = priceFcl.one45 === PriceConstant.NIL ? '' : String(priceFcl.one45)
        break
      case 2:
        qf.eq20 = priceFcl.two20 === PriceConstant.NIL ? '' : String(priceFcl.two20)
        qf.eq40 = priceFcl.two40 === PriceConstant.NIL ? '' : String(priceFcl.two40)
        qf.eqHc = priceFcl.twoHC === PriceConstant.NIL ? '' : String(priceFcl.twoHC)
        qf.eq45 = priceFcl.two45 === PriceConstant.NIL ? '' : String(priceFcl.two45)
        break
      default:
        qf.eq20 = priceFcl.three20 === PriceConstant.NIL ? '' : String(priceFcl.three20)
        qf.eq40 = priceFcl.three40 === PriceConstant.NIL ? '' : String(priceFcl.three40)
        qf.eqHc = priceFcl.threehc === PriceConstant.NIL ? '' : String(priceFcl.threehc)
        qf.eq45 = priceFcl.three45 === PriceConstant.NIL ? '' : String(priceFcl.three45)
        break
    }
    qf.cost20 = qf.eq20
    qf.cost40 = qf.eq40
    qf.costHc = qf.eqHc
    qf.cost45 = qf.eq45
    qf.term = priceFcl.term
    qf.lane = priceFcl.lane
    qf.departureDay = priceFcl.frequency8
    qf.closingDay = ''
    qf.days = String(priceFcl.days)
    qf.beginDate = priceFcl.beginDate
    qf.endDate = priceFcl.endDate
    qf.serviceMode = priceFcl.serviceMode
    qf.currency = priceFcl.currency
    qf.remarks = priceFcl.remarks
    qf.commodity = priceFcl.commodity
    return qf
  }
  static createQuotationLclDetail(priceLcl, level) {
    const ql = new QuotationLclDetail().fillDefaultValue().getData()
    ql.priceLcl = priceLcl
    ql.level = level
    ql.pol = priceLcl.pol
    ql.pod = priceLcl.pod
    ql.via = priceLcl.via
    ql.source = priceLcl.bookingAgent
    ql.term = priceLcl.term
    ql.carrier = String(priceLcl.carrier)
    ql.shippingLine = priceLcl.shippingLine
    ql.departureDay = priceLcl.frequency8
    ql.closingDay = priceLcl.closingDay
    ql.days = priceLcl.days
    ql.currency = priceLcl.currency
    switch (level) {
      case 0:
        ql.weightPrice = priceLcl.costWeightPrice === PriceConstant.NIL ? 0.00 : Number(priceLcl.costWeightPrice)
        ql.volumePrice = priceLcl.costVolumePrice === PriceConstant.NIL ? 0.00 : Number(priceLcl.costVolumePrice)
        break
      case 1:
        ql.weightPrice = priceLcl.oneWeightPrice === PriceConstant.NIL ? 0.00 : Number(priceLcl.oneWeightPrice)
        ql.volumePrice = priceLcl.oneVolumePrice === PriceConstant.NIL ? 0.00 : Number(priceLcl.oneVolumePrice)
        break
      case 2:
        ql.weightPrice = priceLcl.twoWeightPrice === PriceConstant.NIL ? 0.00 : Number(priceLcl.twoWeightPrice)
        ql.volumePrice = priceLcl.twoVolumePrice === PriceConstant.NIL ? 0.00 : Number(priceLcl.twoVolumePrice)
        break
      default:
        ql.weightPrice = priceLcl.threeWeightPrice === PriceConstant.NIL ? 0.00 : Number(priceLcl.threeWeightPrice)
        ql.volumePrice = priceLcl.threeVolumePrice === PriceConstant.NIL ? 0.00 : Number(priceLcl.threeVolumePrice)
        break
    }

    ql.weightCost = ql.weightPrice
    ql.volumeCost = ql.volumePrice
    ql.term = priceLcl.term
    ql.departureDay = priceLcl.frequency8
    ql.closingDay = ''
    ql.days = String(priceLcl.days)
    ql.beginDate = priceLcl.beginDate
    ql.endDate = priceLcl.endDate
    ql.currency = priceLcl.currency
    ql.remarks = priceLcl.remarks
    return ql
  }

  static createQuotationAirDetail(priceAir) {
    const qa = new QuotationAirDetail().fillDefaultValue().getData()
    qa.level = priceAir.level
    qa.flight = priceAir.flight
    qa.aircraft = priceAir.aircraft
    qa.pol = priceAir.pol
    qa.pod = priceAir.pod
    qa.via = priceAir.via
    qa.minCharge = priceAir.minCharge
    qa.step = priceAir.step
    qa.fsc = priceAir.fsc
    qa.ssc = priceAir.ssc
    qa.p45 = priceAir.p45
    qa.p100 = priceAir.p100
    qa.p300 = priceAir.p300
    qa.p500 = priceAir.p500
    qa.p1000 = priceAir.p1000
    qa.carrier = priceAir.carrier
    qa.agent = priceAir.agent
    qa.frequency1 = priceAir.frequency1
    qa.frequency2 = priceAir.frequency2
    qa.frequency3 = priceAir.frequency3
    qa.frequency4 = priceAir.frequency4
    qa.frequency5 = priceAir.frequency5
    qa.frequency6 = priceAir.frequency6
    qa.frequency7 = priceAir.frequency7
    qa.days = priceAir.days
    qa.currency = priceAir.currency
    qa.beginDate = priceAir.beginDate
    qa.endDate = priceAir.endDate
    qa.remarks = priceAir.remarks
    return qa
  }
  static levelLabel(value) {
    if (!value) return ''
    for (const tier of this.TIERS) {
      if (value === tier.value) {
        return tier.label
      }
    }
    return '-'
  }

  static getRegionName(code) {
    const rg = getObjectByCache(TYPE_NAMES.basic_data_Region, 'code', code)
    return rg ? rg.name : code
  }

  static isFrequency(price) {
    const FREQUENCYS = PriceUtils.FREQUENCYS
    const arr = []
    for (const field of FREQUENCYS) {
      if (price['frequency' + field]) {
        arr.push(field)
      }
    }
    return arr.join('/')
  }

  static getSearchPorts(filter, field, moreType) {
    if (field === moreType) {
      let ports = []
      if (!StrUtils.isEmpty(filter[field])) {
        ports = filter[field].split(',')
      }
      return ports
    } else {
      return filter[field]
    }
  }
  static getSeaportEnglishName(data, dataFiled) {
    let code = ''
    switch (dataFiled) {
      case 'pol':
        if (data.hasOwnProperty('isPriceFclAddonEx')) {
          code = data.pol_a || data.pol
        } else {
          code = data.pol
        }
        break
      case 'pod':
        if (data.hasOwnProperty('isPriceFclAddonEx')) {
          code = data.pol_b || data.pod
        } else {
          code = data.pod
        }
        break
      case 'via':
        if (data.hasOwnProperty('isPriceFclAddonEx')) {
          code = data.pod_a || data.pol_b || data.via
        } else {
          code = data.via
        }
        break
      default:
        break
    }
    return ColumnUtils.resolveColumnLabel(basicCacheKeys.basic_data_Seaport, 'code', code, 'englishName')
  }

  static async getPortGroup(code = '') {
    if (!code) return []
    const codes = []
    const item = getObjectByCache(TYPE_NAMES.basic_data_PortGroup, 'portCode', code)
    if (item && item.groupCode) {
      const dataList = await getSpecificBasicData(TYPE_NAMES.basic_data_PortGroup)
      if (dataList && dataList.length > 0) {
        const portGroupList = dataList.filter(p => p.groupCode === item.groupCode)
        if (portGroupList && portGroupList.length > 0) {
          for (const item of portGroupList) {
            if (item.portCode && codes.findIndex(p => p === item.portCode) < 0) {
              codes.push(item.portCode)
            }
          }
        }
      }
    }
    return codes || []
  }
}
